import React, {Fragment, useState} from 'react';
import {
  ButtonGroup,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip
} from "@mui/material";
import styles from "../App.module.scss";
import Button from "@mui/material/Button";
import EditIcon from "@mui/icons-material/Edit";
import Paper from "@mui/material/Paper";
import Downloads from "../components/Downloads/Downloads";
import GridPaper from "../components/ContentBox/GridPaper";
import PreviousVersionLink from "../components/PreviousVersions/PreviousVersionLink";
import {useNavigate, useParams} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {selectInstrumentById, selectIsAuthenticated} from "../store/selectors";
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import {useAreasOfApplication} from "../util/tags";
import DeleteIcon from "@mui/icons-material/Delete";
import FileCopyIcon from '@mui/icons-material/FileCopy';
import {Instrument} from "../gen/api";
import {AppDispatch} from "../store";
import {deleteInstrument} from "../store/reducers/instruments";
import ActionDialog from "../components/ActionDialog/ActionDialog";
import EvaluationRoutines from "../components/EvaluationRoutine/EvaluationRoutines";
import MarkdownViewer from "../components/Markdown/MarkdownViewer";
import PsychometricParameterTableView
  from "../components/PsychometricParameter/PsychometricParameterTableView";
import OverviewTableView from "../components/Overview/OverviewTableView";
import PsychometricParameterRecommendation
  from "../components/PsychometricParameter/PsychometricParameterRecommendation";
import SecondaryHeader from "../components/Header/SecondaryHeader";
import {Print} from "@mui/icons-material";
import { PdfService } from '../services/pdf.service';

type Props = {
  markdown: string | undefined
}

const MarkdownWithExitPrompt = ({ markdown }: Props) => {
  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;

    // Intercept only anchor clicks
    if (target.tagName === 'A') {
      e.preventDefault(); // Stop the browser from navigating

      const url = (target as HTMLAnchorElement).getAttribute('href');
      if (!url) return;

      const confirmLeave = window.confirm("Sie verlassen unsere Plattform. Möchten Sie diese Seite wirklich verlassen?");
      if (confirmLeave) {
        window.open(url, '_blank'); // or use window.location.href = url
      }
    }
  };

  return (
    <div onClick={handleClick}>
      <MarkdownViewer value={markdown} />
    </div>
  );
};


export default function ShowInstrument() {
  const dispatch = useDispatch<AppDispatch>();
  const {instrumentId} = useParams();
  const instrument = useSelector(selectInstrumentById(instrumentId));
  const navigate = useNavigate();
  const areasOfApplication = useAreasOfApplication(instrument?.tagCategories);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const [isActionDialogOpen, setActionDialogOpen] = useState(false);

  const onDelete = (instrument?: Instrument) => {
    if (instrument !== undefined && instrument.id !== undefined) {
      dispatch(deleteInstrument({id: instrument.id, navigateToSearch: () => navigate("/search")}));
      setActionDialogOpen(false);
    }
  };

  return (
      <>
        <Grid container spacing={1}>

          {!instrument &&
              <Grid item xs={12}>
                <Paper>
                  <h3>Das Instrument konnte nicht gefunden werden.</h3>
                </Paper>
              </Grid>
          }

          {instrument &&
              <>
                <SecondaryHeader>
                  <Grid item flex={1} alignSelf="center">
                    <Button sx={{m: "8px 0"}} variant="contained"
                            onClick={() => navigate(`/search`)}>Zurück zur Suchmaske</Button>
                  </Grid>
                  <Grid item flex={1} textAlign="center">
                    <h1 className={styles['content-title']}>{instrument.name}</h1>
                  </Grid>
                  <Grid item flex={1} alignSelf="center" textAlign="end">
                    
                    <ButtonGroup>
                      <Button sx={{m: "8px 0"}}
                        variant="contained"
                        endIcon={<Print/>}
                        onClick={() => PdfService.generatePDF(instrument)}
                      >
                        Drucken
                      </Button>
                      { isAuthenticated && (
                        <>
                        <Button sx={{m: "8px 0"}} variant="contained"
                                endIcon={<EditIcon/>}
                                onClick={() => navigate(`/edit/${instrument?.id}`)}>Bearbeiten</Button>
                        <Tooltip title="Neue Version anlegen (Instrument kopieren)">
                          <Button sx={{m: "8px 0"}}
                                  variant="contained"
                                  endIcon={<FileCopyIcon/>}
                                  onClick={() => navigate(`/new/${instrument?.id}`)}>
                            Kopieren
                          </Button>
                        </Tooltip>
                        <Button sx={{m: "8px 0"}} variant="contained"
                                endIcon={<DeleteIcon/>}
                                onClick={() => setActionDialogOpen(true)}>
                          Löschen
                        </Button></>
                      )}
                    </ButtonGroup>
                    
                  </Grid>
                </SecondaryHeader>

                <GridPaper title={instrument.name} xs={6}>
                  {instrument.publicationYear ?
                      <><b>Publikationsjahr:</b> {instrument.publicationYear}</>
                      : null}
                </GridPaper>

                <GridPaper title="Empfohlener Einsatzbereich" xs={6}>
                  <List>
                    {areasOfApplication?.tags.map(tag =>
                        <ListItem key={tag.id}>
                          <ListItemIcon>{tag.selected ? <CheckBoxIcon/> :
                              <CheckBoxOutlineBlankIcon/>}</ListItemIcon>
                          <ListItemText secondary={tag.name}/>
                        </ListItem>
                    )}
                  </List>
                </GridPaper>

                {instrument.authors ?
                    <GridPaper title="Autoren">
                      <MarkdownViewer value={instrument.authors}/>
                    </GridPaper>
                    : null}

                {(instrument.formOfSurvey || instrument.targetDimension || instrument.rater || instrument.ageRange || instrument.items || instrument.processingTime || instrument.openAccess) ?
                    <OverviewTableView instrument={instrument}/>
                    : null}

                {instrument.diagnosticObjectiveOfTheAssessmentInstrument ?
                    <GridPaper title="Diagnostische Zielsetzung des Assessmentinstruments">
                      <MarkdownWithExitPrompt
                          markdown={instrument.diagnosticObjectiveOfTheAssessmentInstrument}/>
                    </GridPaper>
                    : null}

                {instrument.descriptionOfTheAssessmentInstrument ?
                    <GridPaper title="Beschreibung des Assessmentinstruments">
                      <MarkdownWithExitPrompt markdown={instrument.descriptionOfTheAssessmentInstrument}/>
                    </GridPaper>
                    : null}

                {instrument.psychometricParameterTypes?.map(psy =>
                    <>
                      {psy.psychometricRecommendation || (psy.psychometricParameters && psy.psychometricParameters.length > 0) ?
                            <Fragment key={psy.id}>
                            <PsychometricParameterTableView psychometricParameter={psy}/>
                            {psy.psychometricRecommendation ? (
                              <PsychometricParameterRecommendation
                              psychometricRecommendation={psy.psychometricRecommendation}
                              type={psy.name}/>
                            ) : (
                              <GridPaper xs={5} md={3} xl={2} sx={{textAlign: 'center'}}>
                              <p>Keine Empfehlung verfügbar</p>
                              </GridPaper>
                            )}
                            </Fragment>
                          : null}
                    </>
                )}

                {instrument.notes ?
                    <GridPaper title="Besonderheiten/Anmerkungen">
                      <MarkdownWithExitPrompt markdown={instrument.notes}/>
                    </GridPaper>
                    : null}

                {instrument.languageVersions ?
                    <GridPaper title="Sprachversionen des Assessmentinstruments">
                      <MarkdownWithExitPrompt markdown={instrument.languageVersions}/>
                    </GridPaper>
                    : null}

                {instrument.linkToAssessmentTool ?
                    <GridPaper title="Link zum Assessmentinstrument">
                      <MarkdownWithExitPrompt markdown={instrument.linkToAssessmentTool}/>
                    </GridPaper>
                    : null}

                {instrument.previousVersions.length > 0 ?
                    <GridPaper title="Links zu anderen Versionen">
                      {instrument.previousVersions?.map(instrument =>
                          <ListItem key={instrument.id}>
                            <PreviousVersionLink instrument={instrument} key={instrument.id}/>
                          </ListItem>
                      )}
                    </GridPaper>
                    : null}

                {instrument.originalLiterature ?
                    < GridPaper title="Originalliteratur">
                      <MarkdownViewer value={instrument.originalLiterature}/>
                    </GridPaper>
                    : null}

                {instrument.testDescriptionsAndReviews ?
                    <GridPaper title="Testbeschreibungen/-rezensionen">
                      <MarkdownViewer value={instrument.testDescriptionsAndReviews}/>
                    </GridPaper>
                    : null}

                {instrument.downloadLinks.length > 0 && instrument.forms.length > 0 ?
                    <Downloads instrument={instrument}/>
                    : null}

                {instrument.evaluationRoutines.length > 0 ?
                    <EvaluationRoutines evaluationRoutines={instrument.evaluationRoutines}/>
                    : null}
              </>
          }
        </Grid>
        <ActionDialog isOpen={isActionDialogOpen} onConfirm={() => onDelete(instrument)}
                      onClose={() => setActionDialogOpen(false)}
                      title="Instrument löschen"
                      message="Wollen Sie das Instrument wirklich löschen?"/>
      </>
  );
}